// NodeType.js

// Node Types Cache
import { getNodeType, getNodeTypesCache } from 'services/nodetypes/NodeTypesCacheService'; // same cache the Provider uses

/* NODE TYPE SPEC TEMPLATE

  // One document per type in your _nodetypes collection
  
  MOVIE: {
    key: 'MOVIE',                  // Stable internal key (UPPER_SNAKE). Primary lookup id.
    label: 'Movie',                // User-facing singular label.
    labelPlural: 'Movies',          // Optional plural label.
    dataSection: 'movieinfo',      // Where instance data lives on the node document.

    // UI flags (keep these minimal and consistent)
    showHighlight: false,          // Show "highlight" banner/flag in the node page
    autosizeHighlight: false,      // If true, highlight will autosize
    defaultView: 'LIST',           // Default view for this node type (TABLE, BOARD, GALLERY, LIST, FEED, CALENDAR, TIMELINE, MAP, FORM)

    // Data section template - separate fields for each inherited type
    data_movie: {}                 // Node-type specific data (e.g. data_person)

    // Operational switches (optional, but useful)
    version: 2,                    // bump when you curator-edit a type
    aliases: ['Film'],             // optional search synonyms for the selector

    // Field schema for the form renderer
    // The following field types are already handled elsewhere and should not be
    // in this list: Title, Event Date, Location, Alternate Title, Description
    schema: {
      fields: [
        // --- Basics ---
        { name: 'releaseDate',     label: 'Release Date',     type: 'date' },
        { name: 'runtimeMinutes',  label: 'Runtime (min)',    type: 'number',  min: 1 },
        { name: 'contentRating',   label: 'Content Rating',   type: 'enum',    options: ['G','PG','PG-13','R','NC-17'] },
        { name: 'director',        label: 'Director',         type: 'string',  default: '' },
        { name: 'officialSite',    label: 'Official Site',    type: 'url' },
        { name: 'trailerUrl',      label: 'Trailer URL',      type: 'url' },
        { name: 'rating',          label: 'Rating',           type: 'number',  min: 0, max: 10, default: 10, decimals: 1 },
        { name: 'boxOfficeUSD',    label: 'Box Office',       type: 'number',  min: 0, unit: 'USD' },
        { name: 'hasPostCreditsScene', label: 'Post-credits Scene', type: 'boolean' },
        { name: 'isSequel',        label: 'Is Sequel',        type: 'boolean' },
      ],
    },

    // Priority sort order for child groups
    prioritySort: ["ACTORS", "DIRECTORS", "REVIEWS"],

    // Suggested child nodes for context-aware “Add Content”
    // Keep it as a *static, denormalized list* produced during transform
    // No need for PHOTO, VIDEO, REVIEW, COMMENT, OR LIVE_ROOM
    suggested: [
      { type: 'ACTOR',  label: 'Actor',  multiple: true },
    ],
}
*/

export const ViewType = {
  TABLE: "TABLE",
  BOARD: "BOARD",
  GALLERY: "GALLERY",
  LIST: "LIST",
  FEED: "FEED",
  CALENDAR: "CALENDAR",
  TIMELINE: "TIMELINE",
  SCHEDULER: "SCHEDULER",
  MAP: "MAP",
  FORM: "FORM",
}

/**
 * Returns the schema fields for a node type.
 *
 * @param {string} typeKey - Node type key to resolve.
 * @param {object} [options]
 * @param {boolean} [options.includeHidden=false] - Whether to include fields flagged as hidden.
 * @param {boolean} [options.excludeSecured=false] - Whether to omit fields flagged as secured.
 * @returns {Array<object>} Ordered array of schema field definitions with source metadata.
 */
export function getResolvedFields(typeKey, { includeHidden = false, excludeSecured = false } = {}) {
  if (!typeKey) return [];

  const cache = getNodeTypesCache();
  if (!cache) return [];

  if (typeof typeKey !== 'string') return [];
  const targetKey = typeKey;
  const def = getNodeType(targetKey);
  if (!def) {
    return [];
  }

  const fields = Array.isArray(def?.schema?.fields) ? def.schema.fields : [];
  return fields
    .filter((field) => {
      if (!field?.name || !field.type) return false;
      if (!includeHidden && field.hidden) return false;
      if (excludeSecured && field.secured) return false;
      return true;
    })
    .map((field) => {
      const copy = { ...field };
      if (!copy.source) copy.source = def.key;
      if (copy.inherited == null) copy.inherited = false;
      return copy;
    });
}

/**
 * Detects an item type based on the file MIME type.
 *
 * @param {string} fileType - The MIME type of the file (e.g., "image/jpeg", "application/pdf").
 * 
 * @returns {string} The corresponding item type.
 */
const DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.ms-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-word.document.macroenabled.12',
  'application/vnd.ms-word.template.macroenabled.12',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel.template.macroenabled.12',
  'application/vnd.ms-excel.sheet.binary.macroenabled.12',
  'application/vnd.ms-excel.addin.macroenabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.template',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
  'application/vnd.ms-powerpoint.presentation.macroenabled.12',
  'application/vnd.ms-powerpoint.slideshow.macroenabled.12',
  'application/vnd.ms-powerpoint.template.macroenabled.12',
  'application/vnd.ms-powerpoint.addin.macroenabled.12',
  'application/vnd.ms-powerpoint.slide.macroenabled.12',
  'application/rtf',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.apple.pages',
  'application/vnd.apple.numbers',
  'application/vnd.apple.keynote',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
  'text/plain',
  'text/csv',
  'text/rtf',
  'text/tab-separated-values',
  'text/markdown',
]);

const DOCUMENT_KEYWORDS = [
  'document',
  'word',
  'text',
  'doc',
  'odt',
  'pdf',
  'spreadsheet',
  'sheet',
  'ods',
  'excel',
  'numbers',
  'presentation',
  'slides',
  'powerpoint',
  'ppt',
  'odp',
  'keynote',
  'rtf',
  'csv',
  'tsv',
];

export const detectFileNodeType = (fileType = '') => {
  const safeType = typeof fileType === 'string' ? fileType : '';
  const lowered = safeType.toLowerCase();

  if (lowered.startsWith('image/')) {
    return 'PHOTO';
  }

  if (lowered.startsWith('video/')) {
    return 'VIDEO';
  }

  if (lowered.startsWith('audio/')) {
    return 'GALLERY'; // fallback until dedicated audio type exists
  }

  if (DOCUMENT_MIME_TYPES.has(lowered)) {
    return 'DOCUMENT';
  }

  if (lowered.startsWith('application/') || lowered.startsWith('text/')) {
    if (DOCUMENT_KEYWORDS.some((keyword) => lowered.includes(keyword))) {
      return 'DOCUMENT';
    }
  }

  if (
    lowered.includes('zip') ||
    lowered.includes('compressed') ||
    lowered.includes('tar') ||
    lowered.includes('7z') ||
    lowered.includes('rar') ||
    lowered.includes('gzip')
  ) {
    return 'FILE';
  }

  return 'FILE';
};



